(() => {
  if (customElements.get('shared-product-transition')) return;

  class SharedProductTransition extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      if (this.dataset.page !== 'product') this.prepareCarousel();
    }

    prepareCarousel() {
      this.carousel = this.querySelector('[data-carousel]');
      this.cards = [...this.querySelectorAll('[data-product-card]')];
      this.links = [...this.querySelectorAll('[data-product-transition-link]')];
      this.previousButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.statusNode = this.querySelector('[data-carousel-status]');
      this.activeIndex = 0;
      this.pointerStartX = null;
      this.suppressNextClick = false;

      this.handlePrevious = (event) => {
        event?.preventDefault();
        event?.stopPropagation();
        this.goTo(this.activeIndex - 1);
      };
      this.handleNext = (event) => {
        event?.preventDefault();
        event?.stopPropagation();
        this.goTo(this.activeIndex + 1);
      };

      // Buttons receive their own listeners. Drag capture explicitly ignores them below.
      this.previousButton?.addEventListener('click', this.handlePrevious);
      this.nextButton?.addEventListener('click', this.handleNext);

      this.handleLinkClick = (event) => {
        const link = event.currentTarget;
        const card = link.closest('[data-product-card]');
        const cardIndex = this.cards.indexOf(card);

        if (this.suppressNextClick) {
          event.preventDefault();
          this.suppressNextClick = false;
          return;
        }

        if (cardIndex !== this.activeIndex && this.isPlainPrimaryClick(event)) {
          event.preventDefault();
          this.goTo(cardIndex);
          return;
        }

        // The centered card keeps its native <a> navigation.
      };
      this.links.forEach((link) => link.addEventListener('click', this.handleLinkClick));

      this.handleKeydown = (event) => {
        if (event.key === 'ArrowLeft') this.handlePrevious(event);
        if (event.key === 'ArrowRight') this.handleNext(event);
        if (event.key === 'Home') {
          event.preventDefault();
          this.goTo(0);
        }
        if (event.key === 'End') {
          event.preventDefault();
          this.goTo(this.cards.length - 1);
        }
      };
      this.carousel?.addEventListener('keydown', this.handleKeydown);

      this.handlePointerDown = (event) => {
        if (event.button !== 0) return;
        // Critical: do not capture button presses as carousel drags.
        if (event.target.closest?.('[data-product-action-link], button, input, select, textarea')) return;
        this.pointerStartX = event.clientX;
        this.pointerId = event.pointerId;
        this.pointerDragging = false;
        this.stopAutoplay();
      };
      this.handlePointerMove = (event) => {
        if (this.pointerStartX === null || event.pointerId !== this.pointerId) return;
        if (!this.pointerDragging && Math.abs(event.clientX - this.pointerStartX) > 8) {
          this.pointerDragging = true;
          this.carousel?.setPointerCapture?.(event.pointerId);
        }
      };
      this.handlePointerUp = (event) => {
        if (this.pointerStartX === null) return;
        const distance = event.clientX - this.pointerStartX;
        this.pointerStartX = null;
        if (this.pointerDragging) this.carousel?.releasePointerCapture?.(this.pointerId);
        if (this.pointerDragging && Math.abs(distance) > 45) {
          this.suppressNextClick = true;
          distance < 0 ? this.handleNext() : this.handlePrevious();
          window.setTimeout(() => { this.suppressNextClick = false; }, 120);
        }
        this.pointerDragging = false;
        this.startAutoplay();
      };
      this.handlePointerCancel = () => {
        this.pointerStartX = null;
        this.pointerDragging = false;
        this.startAutoplay();
      };
      this.carousel?.addEventListener('pointerdown', this.handlePointerDown);
      this.carousel?.addEventListener('pointermove', this.handlePointerMove);
      this.carousel?.addEventListener('pointerup', this.handlePointerUp);
      this.carousel?.addEventListener('pointercancel', this.handlePointerCancel);

      this.handleMouseEnter = () => this.stopAutoplay();
      this.handleMouseLeave = () => this.startAutoplay();
      this.handleFocusIn = () => this.stopAutoplay();
      this.handleFocusOut = () => this.startAutoplay();
      this.carousel?.addEventListener('mouseenter', this.handleMouseEnter);
      this.carousel?.addEventListener('mouseleave', this.handleMouseLeave);
      this.carousel?.addEventListener('focusin', this.handleFocusIn);
      this.carousel?.addEventListener('focusout', this.handleFocusOut);

      this.updateCarousel(false);
      this.startAutoplay();
    }

    goTo(index) {
      if (!this.cards.length) return;
      this.activeIndex = (index + this.cards.length) % this.cards.length;
      this.updateCarousel(true);
      if (this.autoplayTimer) {
        this.stopAutoplay();
        this.startAutoplay();
      }
    }

    updateCarousel(announce = true) {
      const count = this.cards.length;
      if (!count) return;

      this.cards.forEach((card, index) => {
        let offset = index - this.activeIndex;
        if (offset > count / 2) offset -= count;
        if (offset < -count / 2) offset += count;
        const distance = Math.abs(offset);
        const active = offset === 0;

        card.dataset.position = this.positionName(offset);
        card.dataset.active = String(active);
        card.dataset.hidden = String(distance > 2.5);
        card.style.setProperty('--spt-layer', String(100 - Math.round(distance * 10)));
        card.style.setProperty('--spt-opacity', String(Math.max(0.28, 1 - distance * 0.18)));
        card.setAttribute('aria-hidden', String(distance > 2.5));
        const link = card.querySelector('[data-product-transition-link]');
        if (link) link.tabIndex = active ? 0 : -1;
      });

      if (this.statusNode) {
        this.statusNode.textContent = `${this.activeIndex + 1} / ${count}`;
        if (announce) {
          const title = this.cards[this.activeIndex]?.querySelector('[data-shared-product-title]')?.textContent?.trim() || '';
          const template = this.dataset.currentProductLabel || '[title]';
          this.statusNode.setAttribute('aria-label', template.replace('[title]', title));
        }
      }
    }

    positionName(offset) {
      if (offset === 0) return 'center';
      if (offset === -1) return 'left';
      if (offset === 1) return 'right';
      if (offset < -1) return 'far-left';
      return 'far-right';
    }

    startAutoplay() {
      if (this.dataset.autoplay !== 'true' || this.cards.length < 2 || this.autoplayTimer) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const delay = Math.max(Number(this.dataset.autoplayDelay || 5000), 3000);
      this.autoplayTimer = window.setInterval(() => this.goTo(this.activeIndex + 1), delay);
    }

    stopAutoplay() {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    isPlainPrimaryClick(event) {
      return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
    }

    disconnectedCallback() {
      this.previousButton?.removeEventListener('click', this.handlePrevious);
      this.nextButton?.removeEventListener('click', this.handleNext);
      this.links?.forEach((link) => link.removeEventListener('click', this.handleLinkClick));
      this.carousel?.removeEventListener('keydown', this.handleKeydown);
      this.carousel?.removeEventListener('pointerdown', this.handlePointerDown);
      this.carousel?.removeEventListener('pointermove', this.handlePointerMove);
      this.carousel?.removeEventListener('pointerup', this.handlePointerUp);
      this.carousel?.removeEventListener('pointercancel', this.handlePointerCancel);
      this.carousel?.removeEventListener('mouseenter', this.handleMouseEnter);
      this.carousel?.removeEventListener('mouseleave', this.handleMouseLeave);
      this.carousel?.removeEventListener('focusin', this.handleFocusIn);
      this.carousel?.removeEventListener('focusout', this.handleFocusOut);
      this.stopAutoplay();
    }
  }

  customElements.define('shared-product-transition', SharedProductTransition);
})();
